import { z } from "zod";
import { GROUP_ROLE, groupRoleSchema } from "./groups.js";

/**
 * Shared DTO cho Role Management — POST /v1/groups/:id/members/:mid/role
 * (US-E4.F6-0028 assign-admin / @UC0019, US-E4.F6-0029 assign-treasurer / @UC0020,
 *  US-E4.F6-0030 remove-role / @UC0019+@UC0020 — GỘP 1 endpoint, Phase 3 RBAC `@gate`).
 *
 * Một nguồn sự thật cho api (validate biên + shape response + mã lỗi) và mobile (gọi + map),
 * tránh drift (DRY). C5: schema validate tại biên tái dùng được.
 *
 * Cross-check API_BUILD_PLAN Phase 3 (dòng 142): verb canonical = POST (DÙNG POST theo plan, dù vài
 * chỗ story ghi PATCH). OWNER-only (R-matrix §9, dòng 146 — Admin/Treasurer/Member KHÔNG đổi role).
 *
 * Logic theo `role` đích (1 endpoint xử lý cả 3 story):
 *  - role='admin'     → bổ nhiệm Admin (0028). Nhiều Admin OK (KHÔNG unique). Treasurer→Admin: slot
 *                       treasurer trống.
 *  - role='treasurer' → bổ nhiệm Treasurer (0029). CHÍNH XÁC 1 treasurer/nhóm — atomic swap: nếu đã
 *                       có treasurer Y khác, Y → member trong cùng transaction (enforce thêm bằng
 *                       partial unique index one_treasurer_per_group + catch P2002).
 *  - role='member'    → thu hồi quyền (0030). admin|treasurer X → member. KHÔNG xoá membership (X vẫn
 *                       active trong nhóm) — KHÁC hẳn DELETE member (0027).
 *
 * Bảo mật (C2/C3): target KHÔNG được gán 'owner' (one_owner invariant — đổi owner là transfer-owner,
 * endpoint khác). target='owner' → cấm (KHÔNG hạ/đổi role owner). KHÔNG tự đổi role chính mình
 * (owner không tự hạ mình gây mất owner). `.strict()` chặn field lạ tại biên.
 */

/**
 * Role được phép GÁN qua endpoint này (KHÔNG có 'owner' — owner chỉ đổi qua transfer-owner; đổi
 * owner ở đây sẽ phá one_owner invariant). Một nguồn sự thật cho request body.
 */
export const assignableRoleSchema = z.enum([
  GROUP_ROLE.ADMIN,
  GROUP_ROLE.TREASURER,
  GROUP_ROLE.MEMBER,
]);

export type AssignableRole = z.infer<typeof assignableRoleSchema>;

/**
 * Body cho POST /v1/groups/:id/members/:mid/role.
 *  - role BẮT BUỘC ∈ {admin, treasurer, member}; 'owner' → reject tại biên (C5 — chống nâng quyền).
 *  - `.strict()` chặn field lạ (chống over-post: KHÔNG nhận userId/status/id từ body — server quyết
 *    target từ path :mid, actor từ token).
 */
export const roleChangeRequestSchema = z
  .object({
    role: assignableRoleSchema,
  })
  .strict();

export type RoleChangeRequest = z.infer<typeof roleChangeRequestSchema>;

/**
 * Mã lỗi máy-đọc-được (RFC7807 problem+json `code`) cho role-management — ổn định qua i18n/đổi
 * wording. Client discriminate theo `code`, KHÔNG so khớp message.
 */
export const ROLE_CHANGE_CODE = {
  /** Cấm đổi role của Owner (one_owner invariant — dùng transfer-owner thay thế). */
  CANNOT_CHANGE_OWNER: "GROUP_ROLE_CANNOT_CHANGE_OWNER",
  /** Cấm tự đổi role chính mình (owner không tự hạ mình gây mất owner). */
  CANNOT_CHANGE_SELF: "GROUP_ROLE_CANNOT_CHANGE_SELF",
  /**
   * 🔴 US-IDM.F1-0288-S05 (@UC9503): cấm gán vai điều hành (treasurer) cho member chưa có tài khoản
   * người dùng (unclaimed / 0 MembershipAccount) — "thủ quỹ ma" không ai chịu trách nhiệm tiền.
   */
  MEMBER_UNCLAIMED: "GROUP_MEMBER_UNCLAIMED",
} as const;

/** Thông điệp lỗi (tiếng Việt sản phẩm) — một nguồn sự thật api ↔ mobile. */
export const CANNOT_CHANGE_OWNER_ROLE_MESSAGE =
  "Không thể đổi vai trò của chủ nhóm.";
export const CANNOT_CHANGE_SELF_ROLE_MESSAGE =
  "Bạn không thể tự đổi vai trò của chính mình.";

/**
 * Shape response cho POST /v1/groups/:id/members/:mid/role (đổi role thành công).
 *  - memberId: GroupMember.id của target đã đổi role.
 *  - userId: User.id chủ membership.
 *  - role: vai trò SAU khi đổi (admin|treasurer|member).
 *  - replacedTreasurerUserId: khi gán treasurer mà có treasurer cũ Y bị hạ về member (atomic swap),
 *    trả userId của Y để client cập nhật badge ngay; null khi không có swap.
 * `.strict()` chặn rò field thừa (vd PII/secret) ra response (C2).
 */
export const roleChangeResponseSchema = z
  .object({
    memberId: z.string().uuid(),
    userId: z.string().uuid(),
    role: groupRoleSchema,
    replacedTreasurerUserId: z.string().uuid().nullable(),
  })
  .strict();

export type RoleChangeResponse = z.infer<typeof roleChangeResponseSchema>;

/**
 * Shared DTO cho Transfer Ownership — POST /v1/groups/:id/ownership/transfer
 * (US-APP-IMP.F1-0142 / @UC0144). Đổi CHỦ nhóm: owner hiện tại chọn 1 member active cùng nhóm làm
 * owner mới; owner cũ hạ về admin (PO chốt 06/07 — giữ owner cũ trong nhóm với quyền quản trị cao,
 * thân thiện luồng self-leave 0141). Transfer CÓ HIỆU LỰC NGAY (MVP — KHÔNG có bước accept).
 *
 * KHÁC roleChangeRequestSchema: transfer nhận `newOwnerMemberId` (GroupMember.id của owner mới),
 * KHÔNG nhận role. Endpoint riêng vì đổi owner phá one_owner nếu không swap atomic (S02 lo swap
 * Serializable + catch P2034/P2002). Một nguồn sự thật api ↔ mobile (DRY).
 *
 * Body: `{ newOwnerMemberId }` — `.strict()` chặn field lạ (C5 chống over-post: KHÔNG nhận
 * role/userId/status/id; server quyết actor từ token, target từ newOwnerMemberId).
 */
export const transferOwnershipRequestSchema = z
  .object({
    newOwnerMemberId: z.string().uuid(),
  })
  .strict();

export type TransferOwnershipRequest = z.infer<
  typeof transferOwnershipRequestSchema
>;

/**
 * Response transfer thành công:
 *  - groupId: nhóm đã đổi chủ.
 *  - newOwnerMemberId: GroupMember.id owner mới (đã là 'owner').
 *  - previousOwnerMemberId: GroupMember.id owner cũ (actor, đã hạ).
 *  - previousOwnerNewRole: role owner cũ SAU khi hạ (='admin' theo PO chốt).
 * `.strict()` chặn rò field thừa ra response (C2).
 */
export const transferOwnershipResponseSchema = z
  .object({
    groupId: z.string().uuid(),
    newOwnerMemberId: z.string().uuid(),
    previousOwnerMemberId: z.string().uuid(),
    previousOwnerNewRole: groupRoleSchema,
  })
  .strict();

export type TransferOwnershipResponse = z.infer<
  typeof transferOwnershipResponseSchema
>;

/**
 * Mã lỗi máy-đọc-được cho transfer ownership (RFC7807 `code`) — client discriminate theo code.
 */
export const TRANSFER_OWNERSHIP_CODE = {
  /** Target = chính owner đang gọi (self) → không thể chuyển cho chính mình. */
  CANNOT_TRANSFER_TO_SELF: "GROUP_OWNERSHIP_CANNOT_TRANSFER_TO_SELF",
  /**
   * 🔴 US-E24.F3-0352 HIGH-D(2) — target là ghế KHÔNG AI đăng nhập được (0 `primaryUserId` và 0
   * `MembershipAccount`) ⇒ 409. Chuyển quyền vào ghế đó = nhóm **mất owner vĩnh viễn**: owner cũ đã
   * bị hạ `admin` trong cùng thao tác, còn slot `one_owner_per_group` bị ghế chết chiếm.
   * Cùng lý lẽ với luật "thủ quỹ ma" đã có ở `role-management.service.ts` — vai điều hành phải có
   * người thật đứng sau.
   */
  TARGET_UNCLAIMED: "GROUP_OWNERSHIP_TARGET_UNCLAIMED",
} as const;

/** Thông điệp lỗi (tiếng Việt sản phẩm) — một nguồn sự thật api ↔ mobile. */
export const CANNOT_TRANSFER_TO_SELF_MESSAGE =
  "Bạn không thể chuyển quyền sở hữu cho chính mình.";
/** Chỉ đường sang việc phải làm trước, không chỉ nói cái bị cấm (§7 error model). */
export const TRANSFER_TARGET_UNCLAIMED_MESSAGE =
  "Không thể chuyển quyền sở hữu cho suất chưa có người đại diện đăng nhập được. " +
  "Hãy gắn tài khoản cho suất đó trước.";
